// Derives the read-only "Clinical Recommendations" shown in the Catalight
// submission dialog (service type + per-tier hours) from the report's export
// data.
//
// PROA-1261: BE confirmed these values are NOT exposed as clean report fields —
// they live in the report's template content, in the `Catalight_Treatment_Options`
// section, and are read from the same place the PDF export already reads them:
//
//   - Service type:  the `*_Mediated_Treatment_Option_Select` SELECT fields,
//                    whose `content` JSON is `{ options: [...], answer: string|null }`.
//   - Per-tier hours: the `*_Mediated_Treatment_Option` TREATMENT_OPTION_TABLE
//                    fields, whose `content` JSON is a `PractitionerDataRow[]`
//                    (the same shape the docx export parses).
//
// Per product direction the frontend must NOT translate the selected values into
// Catalight's internal terminology — the backend handles that at submission time.
// We only read the raw answers and combine them for display.
import { PractitionerDataRow } from "@components/ReportField/inputs/ReportFieldTreatmentOptionTable";

// Known Catalight field identifiers in the `Catalight_Treatment_Options` section.
// The new MBL template renamed the fields relative to earlier templates, so each
// lookup tries the new name first and falls back to the old name:
//   Select: new = *_Option (no suffix)       old = *_Option_Select
//   Table:  new = *_Treatment_Table          old = *_Treatment_Option
const SELECTED_TREATMENT_OPTION_FIELD = "Selected_Treatment_Option";
const PRACTITIONER_SELECT_FIELD = "Practitioner_Mediated_Treatment_Option";
const PRACTITIONER_SELECT_FIELD_LEGACY = "Practitioner_Mediated_Treatment_Option_Select";
const CAREGIVER_SELECT_FIELD = "Caregiver_Mediated_Treatment_Option";
const CAREGIVER_SELECT_FIELD_LEGACY = "Caregiver_Mediated_Treatment_Option_Select";
const PRACTITIONER_TABLE_FIELD = "Practitioner_Mediated_Treatment_Table";
const PRACTITIONER_TABLE_FIELD_LEGACY = "Practitioner_Mediated_Treatment_Option";
const CAREGIVER_TABLE_FIELD = "Caregiver_Mediated_Treatment_Table";
const CAREGIVER_TABLE_FIELD_LEGACY = "Caregiver_Mediated_Treatment_Option";

// The four tiers the dialog can show, in display order. Keys match the labels
// the dialog renders for both the read-only recommendations and the editable
// offer inputs.
export type CatalightTierKey = "para" | "mid" | "high" | "group";
const TIER_DISPLAY_ORDER: CatalightTierKey[] = ["para", "mid", "high", "group"];

// PractitionerDataRow.level (as authored in the report) → dialog tier key.
const LEVEL_TO_TIER: Record<string, CatalightTierKey> = {
  Paraprofessional: "para",
  "Mid-Level Supervisor": "mid",
  "High Level Supervisor": "high",
  "Social Skills Group": "group",
};

// Tier set for a SINGLE selected service. Compound single options (e.g.
// "Paraprofessional ABA & Social Skills Group") are still handled here via
// substring matching:
//   - default                                      → Paraprofessional, Mid, High
//   - "...Led ABA" (no Paraprofessional component)  → Mid, High only
//   - any "Social Skills" plan                      → also Group Care
const allowedTiersForSingleService = (service: string): CatalightTierKey[] => {
  const s = service.toLowerCase();
  const isLedAba = s.includes("led aba") && !s.includes("paraprofessional");
  const base: CatalightTierKey[] = isLedAba ? ["mid", "high"] : ["para", "mid", "high"];
  return s.includes("social skills") ? [...base, "group"] : base;
};

// Derives the allowed tier set from the already-resolved serviceType string
// (sourced from Selected_Treatment_Option). Returns null (= no restriction, show
// all populated tiers) when:
//   - serviceType is unknown/null — can't restrict without a signal
//   - serviceType is a combined label containing " or " or " & " — both services
//     are selected, so we trust whatever the BCBA entered in the tables
// Otherwise delegates to allowedTiersForSingleService.
//
// Using serviceType here (rather than the raw *_Select fields) ensures tier
// visibility stays consistent with the canonical field — old *_Select values
// that contradict Selected_Treatment_Option no longer silently filter hours out.
const allowedTiersForServiceType = (sType: string | null): CatalightTierKey[] | null => {
  if (!sType) return null;
  const lower = sType.toLowerCase();
  // Only skip filtering for true dual-service labels (practitioner + caregiver combined).
  // Compound single-service labels like "Paraprofessional ABA & Social Skills Group"
  // should still go through allowedTiersForSingleService.
  const isDualService = (lower.includes(" or ") || lower.includes(" & ")) && lower.includes("caregiver");
  if (isDualService) return null;
  return allowedTiersForSingleService(sType);
};

export interface MediatedServices {
  /** Practitioner-mediated service, e.g. "Paraprofessional ABA". */
  practitioner?: string | null;
  /** Caregiver-mediated service, e.g. "Caregiver Led ABA". */
  caregiver?: string | null;
}

// Combines the two mediated-service values for display:
//   practitioner only → practitioner
//   caregiver only    → caregiver
//   both              → "<practitioner> & <caregiver>"
//   neither           → null (caller renders the muted fallback)
export const resolveServiceType = ({ practitioner, caregiver }: MediatedServices): string | null => {
  const p = practitioner?.trim() || null;
  const c = caregiver?.trim() || null;
  if (p && c) return `${p} & ${c}`;
  return p ?? c ?? null;
};

// Minimal structural shape of the `reportExportData.sections` returned by the
// query — kept loose so the parser stays decoupled from the generated types and
// easy to unit-test.
interface ExportFieldLike {
  field_identifier?: string | null;
  content?: string | null;
  field_type?: string | null;
}
interface ExportSectionLike {
  key?: string | null;
  fields?: Array<ExportFieldLike | null> | null;
}
type ExportSections = Array<ExportSectionLike | null> | null | undefined;

// Numeric bounds the editable offer is validated against. A range recommendation
// yields min < max; an exact recommendation yields min === max with `exact: true`,
// which the dialog enforces as "must match exactly".
export interface CatalightTierBounds {
  min: number;
  max: number;
  exact: boolean;
}

export interface CatalightTierRecommendation {
  key: CatalightTierKey;
  /** e.g. "8 - 10 hours/mo" or "4 hours/mo". */
  hoursText: string;
  /** Numeric bounds for offer validation (always present for an emitted tier). */
  bounds: CatalightTierBounds;
}

export interface CatalightRecommendations {
  serviceType: string | null;
  /** Merged first-wins tiers — drives offer inputs and validation. */
  tiers: CatalightTierRecommendation[];
  /** Practitioner table tiers only — drives the read-only display when both services are selected. */
  practitionerTiers: CatalightTierRecommendation[];
  /** Caregiver table tiers only — drives the read-only display when both services are selected. */
  caregiverTiers: CatalightTierRecommendation[];
}

// Finds the `content` of the first field with the given identifier across all
// sections (the treatment-option fields all live in `Catalight_Treatment_Options`,
// but we search broadly to stay resilient to section reorganization).
// Pass `fieldType` to disambiguate when two templates share the same identifier
// for fields of different types (e.g. the new SELECT "Practitioner_Mediated_Treatment_Option"
// and the legacy TABLE of the same name).
const findFieldContent = (sections: ExportSections, identifier: string, fieldType?: string): string | null => {
  for (const section of sections ?? []) {
    for (const field of section?.fields ?? []) {
      if (field?.field_identifier === identifier && (!fieldType || field?.field_type === fieldType)) {
        return field.content ?? null;
      }
    }
  }
  return null;
};

const parseSelectAnswer = (content: string | null): string | null => {
  if (!content) return null;
  try {
    const parsed = JSON.parse(content) as { answer?: unknown };
    return typeof parsed.answer === "string" && parsed.answer.trim() ? parsed.answer.trim() : null;
  } catch {
    return null;
  }
};

const parseTreatmentRows = (content: string | null): PractitionerDataRow[] => {
  if (!content) return [];
  try {
    const parsed: unknown = JSON.parse(content);
    return Array.isArray(parsed) ? (parsed as PractitionerDataRow[]) : [];
  } catch {
    return [];
  }
};

// Builds the display string and numeric bounds for one tier row, or null when the
// row has no usable value. The bounds drive offer validation; the text drives the
// read-only Clinical Recommendations display.
const tierValue = (row: PractitionerDataRow): Pick<CatalightTierRecommendation, "hoursText" | "bounds"> | null => {
  if (row.isRange) {
    if (row.rangeMin == null || row.rangeMax == null) return null;
    return {
      hoursText: `${row.rangeMin} - ${row.rangeMax} hours/mo`,
      bounds: { min: row.rangeMin, max: row.rangeMax, exact: row.rangeMin === row.rangeMax },
    };
  }
  if (row.hour == null) return null;
  return {
    hoursText: `${row.hour} ${row.hour === 1 ? "hour" : "hours"}/mo`,
    bounds: { min: row.hour, max: row.hour, exact: true },
  };
};

// Returns true when the service type indicates no treatment was recommended
// (e.g. "No treatment recommended"). When true the dialog hides all tier rows
// and the Updated Offer inputs — only comments remain.
// Anchored so a combined label that happens to contain this phrase as a substring
// (e.g. a legacy fallback value) doesn't falsely clear all hours.
export const isNoTreatmentRecommended = (serviceType: string | null): boolean =>
  /^no.*treatment.*recommended$/i.test(serviceType?.trim() ?? "");

// Determines which hours groups to show in the Clinical Recommendations card.
//   "practitioner-only"  — single practitioner service; or compound social-skills with no
//                          caregiver (e.g. "Paraprofessional ABA & Social Skills Group")
//   "caregiver-only"     — "caregiver" present without separator; or compound social-skills
//                          with a caregiver component (e.g. "Caregiver Led ABA & Social Skills Group")
//   "both"               — true dual-service (" & "/"or" + "caregiver"); standalone
//                          "Social Skills Group"; or unknown/null
export type ServiceDisplay = "practitioner-only" | "caregiver-only" | "both";

export const resolveServiceDisplay = (serviceType: string | null): ServiceDisplay => {
  if (!serviceType) return "both";
  const lower = serviceType.toLowerCase();
  const hasSeparator = lower.includes(" & ") || lower.includes(" or ");
  const hasCaregiver = lower.includes("caregiver");

  // Compound social-skills options: the accompanying service(s) determine the group.
  if (lower.includes("social skills") && hasSeparator) {
    if (!hasCaregiver) return "practitioner-only";
    // Dual-service when " or " is used, or when "social skills" is listed first
    // (meaning it IS the practitioner service, e.g. "Social Skills Group & Caregiver Training"
    // from the legacy resolveServiceType fallback that combines two SELECT fields with " & ").
    if (lower.includes(" or ") || lower.indexOf("social skills") < lower.indexOf("caregiver")) return "both";
    return "caregiver-only";
  }

  // Standalone "Social Skills Group" → show both groups
  if (lower.includes("social skills")) return "both";

  // True dual-service combined labels (practitioner + caregiver, no social skills)
  if (hasSeparator && hasCaregiver) return "both";

  if (hasCaregiver) return "caregiver-only";
  return "practitioner-only";
};

// Resolves the read-only recommendations from the report's export sections.
// Each treatment table is parsed independently so the dialog can display
// separate "Practitioner-Mediated" and "Caregiver-Mediated" groups when both
// services are selected. The merged `tiers` (practitioner wins for shared tier
// keys) drives the editable offer inputs, which always use a single flat set.
export const resolveCatalightRecommendations = (sections: ExportSections): CatalightRecommendations => {
  // Use field_type: "SELECT" on all select lookups so the new-template SELECT
  // "Practitioner_Mediated_Treatment_Option" is not confused with the legacy-template
  // TABLE that shares the same identifier.
  const mediatedServices: MediatedServices = {
    practitioner:
      parseSelectAnswer(findFieldContent(sections, PRACTITIONER_SELECT_FIELD, "SELECT")) ??
      parseSelectAnswer(findFieldContent(sections, PRACTITIONER_SELECT_FIELD_LEGACY, "SELECT")),
    caregiver:
      parseSelectAnswer(findFieldContent(sections, CAREGIVER_SELECT_FIELD, "SELECT")) ??
      parseSelectAnswer(findFieldContent(sections, CAREGIVER_SELECT_FIELD_LEGACY, "SELECT")),
  };
  // Primary: purpose-built combined field (correct label, includes "No treatment
  // recommended", no N/A option). Fallback: derive from the individual fields for
  // reports predating Selected_Treatment_Option.
  const serviceType =
    parseSelectAnswer(findFieldContent(sections, SELECTED_TREATMENT_OPTION_FIELD, "SELECT")) ??
    resolveServiceType(mediatedServices);

  // When no treatment is recommended there are no hours to show or offer.
  if (isNoTreatmentRecommended(serviceType)) {
    return { serviceType, tiers: [], practitionerTiers: [], caregiverTiers: [] };
  }

  type TierValueMap = Map<CatalightTierKey, Pick<CatalightTierRecommendation, "hoursText" | "bounds">>;
  const buildTierMap = (rows: PractitionerDataRow[]): TierValueMap => {
    const m: TierValueMap = new Map();
    for (const row of rows) {
      const key = LEVEL_TO_TIER[row.level];
      if (!key || m.has(key)) continue;
      const value = tierValue(row);
      if (value) m.set(key, value);
    }
    return m;
  };

  // Compute allowed tiers once — applied consistently to all three output arrays
  // (practitionerTiers, caregiverTiers, and merged tiers) so the display groups
  // and the offer inputs always show the same set of levels.
  const allowed = allowedTiersForServiceType(serviceType);

  const toTiers = (m: TierValueMap): CatalightTierRecommendation[] =>
    TIER_DISPLAY_ORDER.filter((key) => m.has(key) && (allowed === null || allowed.includes(key))).map((key) => ({
      key,
      ...m.get(key)!,
    }));

  const practitionerRawRows = parseTreatmentRows(
    findFieldContent(sections, PRACTITIONER_TABLE_FIELD) ?? findFieldContent(sections, PRACTITIONER_TABLE_FIELD_LEGACY),
  );
  const caregiverRawRows = parseTreatmentRows(
    findFieldContent(sections, CAREGIVER_TABLE_FIELD) ?? findFieldContent(sections, CAREGIVER_TABLE_FIELD_LEGACY),
  );
  const practitionerMap = buildTierMap(practitionerRawRows);
  const caregiverMap = buildTierMap(caregiverRawRows);

  // Merged map: caregiver first so practitioner overwrites shared tier keys.
  const mergedMap: TierValueMap = new Map([...caregiverMap, ...practitionerMap]);

  const tiers = TIER_DISPLAY_ORDER.filter(
    (key) => mergedMap.has(key) && (allowed === null || allowed.includes(key)),
  ).map((key) => ({ key, ...mergedMap.get(key)! }));

  return {
    serviceType,
    tiers,
    practitionerTiers: toTiers(practitionerMap),
    caregiverTiers: toTiers(caregiverMap),
  };
};
